// Creative Studio direct-to-storage upload client.
//
// WHY THIS EXISTS
// ---------------
// Creative Studio used to POST each file as a multipart body to the Cphere
// backend, which buffered the whole thing in memory. On a 512MB service that
// meant every one of the six "Never Stop Chasing" masters (274.3MB-716.6MB)
// killed the process mid-request; the browser saw a bare transport error and
// the UI could only say "Upload failed".
//
// Now the browser talks to storage directly:
//   1. POST /creative-assets/upload-sessions  -> a per-file resumable session
//   2. PUT  <session uri>                     -> 8MiB chunks, straight to
//                                                Google, never via Cphere
//   3. POST /creative-assets/upload-sessions/:id/finalize
//                                             -> the backend verifies what
//                                                storage holds and creates
//                                                the creative
//
// Only step 2 carries creative bytes, and it never touches our servers. Every
// file is an independent session, so one failure cannot affect the others.
//
// This module is deliberately framework-free (no React, no app imports) so
// the whole upload contract is unit-testable without a DOM.

/** Chunk boundaries Google requires: every non-final chunk is a 256KiB multiple. */
const CHUNK_ALIGNMENT = 256 * 1024;

/** How many times one chunk is re-sent before the file is called failed. */
const MAX_CHUNK_ATTEMPTS = 4;

/** Retry backoff. Short, bounded, and jitter-free so tests stay deterministic. */
const RETRY_BASE_MS = 500;

// The transport's observable phases.
//
// RESUMING and VERIFYING exist because the interface has to tell two waits
// apart that used to look identical: "your bytes already exist remotely and we
// are continuing" and "your bytes are all there and the server is checking
// them". Both are real, distinguishable moments in this protocol — nothing
// here is emitted speculatively.
export const UPLOAD_PHASES = Object.freeze({
  QUEUED: "queued",
  PREPARING: "preparing",
  RESUMING: "resuming",
  UPLOADING: "uploading",
  VERIFYING: "verifying",
  FINALIZING: "finalizing",
  DONE: "done",
  FAILED: "failed",
  SKIPPED: "skipped",
});

/**
 * Round a chunk size to something Google accepts. An unaligned chunk makes
 * the whole transfer fail at the last moment, which is exactly the kind of
 * late failure this rewrite exists to eliminate.
 */
export function alignChunkSize(bytes) {
  const numeric = Number(bytes);
  if (!Number.isFinite(numeric) || numeric < CHUNK_ALIGNMENT) return CHUNK_ALIGNMENT;
  return Math.floor(numeric / CHUNK_ALIGNMENT) * CHUNK_ALIGNMENT;
}

/**
 * Byte formatting identical to the backend's: BINARY divisor with
 * decimal-style labels, because that is what Google Drive displays. The
 * production master is 751,399,812 bytes and Drive shows "716.6 MB"; a
 * decimal formatter would call the same file "751.4 MB" and look like a
 * different object.
 */
export function formatBytes(bytes) {
  const numeric = Number(bytes);
  if (!Number.isFinite(numeric) || numeric < 0) return "an unknown size";
  if (numeric < 1024) return `${Math.trunc(numeric)} bytes`;
  const units = ["KB", "MB", "GB", "TB"];
  let value = numeric / 1024;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  const rounded = Math.round(value * 10) / 10;
  return `${Number.isInteger(rounded) ? rounded : rounded.toFixed(1)} ${units[unitIndex]}`;
}

const extensionOf = (name) => {
  const match = String(name || "").toLowerCase().match(/\.([a-z0-9]+)$/);
  return match ? match[1] : null;
};

/**
 * Pre-upload validation against the SERVER'S policy.
 *
 * The policy object is fetched from the backend rather than hardcoded, so the
 * browser can never disagree with the server about what is allowed — the
 * failure mode this replaces was a UI that happily started a 716.6MB upload
 * the server could not survive.
 */
export function validateFileAgainstPolicy(file, policy) {
  if (!policy) return { accepted: true };

  const name = String(file?.name || "").trim();
  const size = Number(file?.size);

  if (!name) {
    return { accepted: false, code: "creative_upload_missing_filename", message: "This file has no name and cannot be uploaded." };
  }
  if (!Number.isFinite(size) || size <= 0) {
    return { accepted: false, code: "creative_upload_empty_file", message: `"${name}" is empty and cannot be uploaded.` };
  }
  if (size > policy.maxSourceFileBytes) {
    return {
      accepted: false,
      code: "creative_upload_file_too_large",
      message: `This file is ${formatBytes(size)}. Cphere currently supports video uploads up to ${policy.maxSourceFileLabel}.`,
    };
  }

  const extension = extensionOf(name);
  const accepted = Array.isArray(policy.acceptedExtensions) ? policy.acceptedExtensions : [];
  if (accepted.length && (!extension || !accepted.includes(extension))) {
    return {
      accepted: false,
      code: "creative_upload_unsupported_extension",
      message: extension
        ? `Cphere does not support .${extension} creative files.`
        : `"${name}" has no file extension, so its type cannot be established.`,
    };
  }

  return { accepted: true };
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Progress reporting is throttled because each update drives React state in a
 * very large page. A 716.6 MB file in 8 MiB chunks emits ~90 updates and a
 * six-file selection several hundred; unthrottled, every one of them
 * re-rendered the whole Creative Studio tree, which is why the dashboard
 * became sluggish during uploads. Phase changes and completion always pass
 * through immediately — only mid-transfer byte ticks are rate-limited.
 */
const PROGRESS_THROTTLE_MS = 250;

function throttleProgress(onProgress) {
  let lastAt = 0;
  let lastPhase = null;
  return (update) => {
    const phaseChanged = update.phase !== lastPhase;
    const terminal =
      update.phase === UPLOAD_PHASES.DONE ||
      update.phase === UPLOAD_PHASES.FAILED ||
      update.phase === UPLOAD_PHASES.FINALIZING ||
      // RESUMING and VERIFYING each occur exactly once and mark a change of
      // wait, not a byte tick. Rate-limiting them could drop the only signal
      // the user gets that the upload moved on.
      update.phase === UPLOAD_PHASES.RESUMING ||
      update.phase === UPLOAD_PHASES.VERIFYING;
    const now = Date.now();
    if (phaseChanged || terminal || now - lastAt >= PROGRESS_THROTTLE_MS) {
      lastAt = now;
      lastPhase = update.phase;
      onProgress(update);
    }
  };
}

/**
 * PUT one chunk. Resolves with the storage response so the caller can tell
 * "keep going" (308) from "the object is complete" (200/201).
 */
async function putChunk({ uploadUrl, blob, start, end, total, signal, fetchImpl }) {
  const response = await fetchImpl(uploadUrl, {
    method: "PUT",
    headers: { "Content-Range": `bytes ${start}-${end - 1}/${total}` },
    body: blob,
    signal,
  });
  return response;
}

/**
 * Ask CPHERE'S SERVER what storage actually holds.
 *
 * THE BROWSER CANNOT ASK GOOGLE DIRECTLY, and that is the entire defect this
 * replaces. Google returns CORS headers on intermediate `308 Resume
 * Incomplete` responses but NOT on the terminal `200/201` that completes an
 * upload — verified against production sessions 723f9b89… and 215b5412…:
 * the preflight allows the origin, the 200 carries no
 * Access-Control-Allow-Origin. So the response that says "your upload
 * finished" is unreadable from a page; `fetch` rejects it as a network
 * error. Two uploads that had completely succeeded were reported as lost
 * because of exactly this.
 *
 * The server has no CORS restriction, so it is the only component that can
 * tell the difference between finished and interrupted. It also persists the
 * confirmed offset, so resume position lives in Cphere's own record rather
 * than in whatever the browser happened to observe.
 *
 * States: complete | partial | empty | expired | unknown.
 */
export async function probeSessionState({ api, sessionId, advertiserId }) {
  try {
    const result = await api.getUploadSessionState({ sessionId, advertiserId });
    return {
      state: result?.state || "unknown",
      confirmed: Number(result?.confirmedBytes) || 0,
      storageFileId: result?.storageFileId || null,
      creativeAssetId: result?.creativeAssetId || null,
    };
  } catch (_error) {
    // Cphere itself was unreachable. Not knowing is not the same as knowing
    // nothing was saved, and must never be reported as such.
    return { state: "unknown", confirmed: 0 };
  }
}

/**
 * Upload one file, end to end.
 *
 * Reports progress per file. Never throws for an expected failure — it
 * resolves with a result object, so one bad file in a selection of six can
 * never abort the others.
 */
export async function uploadOneCreative({
  file,
  advertiserId,
  title,
  notes,
  batchId,
  policy,
  metadata = null,
  api,
  fetchImpl = fetch,
  signal = null,
  onProgress = () => {},
}) {
  const name = String(file?.name || "file");
  const report_ = throttleProgress(onProgress);

  const validation = validateFileAgainstPolicy(file, policy);
  if (!validation.accepted) {
    // Rejected before a single byte moves: the user learns the real reason
    // immediately instead of after a long transfer.
    report_({ name, phase: UPLOAD_PHASES.FAILED, loaded: 0, sent: 0, total: file?.size || 0 });
    return { ok: false, name, code: validation.code, message: validation.message, uploaded: false };
  }

  const total = Number(file.size);
  report_({ name, phase: UPLOAD_PHASES.PREPARING, loaded: 0, sent: 0, total });

  let session;
  try {
    session = await api.createUploadSession({
      advertiserId,
      filename: name,
      mimeType: file.type || "",
      sizeBytes: total,
      title,
      notes,
      batchId,
      metadata,
    });
  } catch (error) {
    return {
      ok: false,
      name,
      code: error?.code || "creative_upload_session_failed",
      message: error?.message || "This upload could not be started.",
      uploaded: false,
    };
  }

  // An idempotent replay of an already-finished file: nothing to transfer.
  if (session?.status === "FINALIZED" && session?.creativeAssetId) {
    report_({ name, phase: UPLOAD_PHASES.DONE, loaded: total, sent: total, total });
    return { ok: true, name, assetId: session.creativeAssetId, replayed: true, uploaded: false };
  }

  const uploadUrl = session?.uploadUrl;
  if (!uploadUrl) {
    return {
      ok: false,
      name,
      code: "creative_upload_no_capability",
      message: "Storage did not provide an upload location for this file.",
      uploadSessionId: session?.id || null,
    };
  }

  const chunkBytes = alignChunkSize(session.chunkBytes || policy?.chunkBytes);

  // Two different quantities, deliberately kept apart. `confirmed` is what
  // STORAGE has acknowledged and is the only thing durable across a
  // disconnect; `sent` is what this browser has pushed onto the wire and may
  // evaporate. Progress that survives a failure must report `confirmed`,
  // otherwise a bar can sit at 99% describing bytes nobody kept.
  let confirmed = 0;
  let sent = 0;

  const report = (phase) =>
    report_({ name, phase, loaded: confirmed, sent, total });
  // Resume from whatever storage already holds (0 for a fresh session).
  const opening = await probeSessionState({ api, sessionId: session.id, advertiserId });
  if (opening.state === "complete") {
    // The object is already whole — a previous attempt delivered every byte
    // and only its response was lost. Skip straight to finalization rather
    // than re-uploading hundreds of megabytes.
    confirmed = total;
    sent = total;
  } else if (opening.state === "partial") {
    confirmed = opening.confirmed;
    sent = opening.confirmed;
  } else if (opening.state === "expired") {
    return {
      ok: false,
      name,
      code: "creative_upload_session_expired",
      message:
        "Cphere needs to reconnect this creative before continuing.",
      title: "Upload session expired",
      action: "retry",
      confirmedBytes: 0,
      total,
      uploadSessionId: session.id,
    };
  }

  // Storage already holds part of this file, so the transfer is CONTINUING,
  // not starting. Saying so before the byte events restart is what stops the
  // bar from appearing to begin a 716.6 MB upload over again. Unlike the
  // browser-side guess this replaces, `confirmed` is the offset Cphere's own
  // server persisted — the only number that survives a disconnect.
  if (confirmed > 0 && confirmed < total) {
    report(UPLOAD_PHASES.RESUMING);
  }

  report(UPLOAD_PHASES.UPLOADING);

  // Bounded consecutive stalls. Reset by any real forward progress, so a long
  // upload is never capped on total retries — only on getting stuck.
  let stalledAttempts = 0;

  while (confirmed < total) {
    if (signal?.aborted) {
      return {
        ok: false,
        name,
        code: "creative_upload_cancelled",
        title: "Upload cancelled",
        message: "Upload cancelled.",
        action: "resume",
        confirmedBytes: confirmed,
        total,
        uploadSessionId: session.id,
      };
    }

    const end = Math.min(confirmed + chunkBytes, total);
    // One slice at a time — the browser holds a chunk, never the whole file.
    const blob = file.slice(confirmed, end);

    let response = null;
    try {
      response = await putChunk({
        uploadUrl, blob, start: confirmed, end, total, signal, fetchImpl,
      });
      // 5xx is transient; treat it like a lost response rather than a verdict.
      if (response.status >= 500) response = null;
    } catch (_error) {
      response = null;
    }

    if (!response) {
      // NEVER blindly re-send. The bytes may already be durable — on the
      // terminal chunk they almost certainly are, because Google's completion
      // response is CORS-blocked and always arrives here as a "failure".
      // Re-sending would waste an 8 MiB round trip per attempt and still not
      // learn anything. Ask the server, which can actually see the truth.
      const probe = await probeSessionState({ api, sessionId: session.id, advertiserId });

      if (probe.state === "complete") {
        confirmed = total;
        sent = total;
        break;
      }

      if (probe.state === "partial" && probe.confirmed > confirmed) {
        // Storage kept what we just sent (or part of it). Continue from the
        // confirmed position instead of repeating work.
        confirmed = probe.confirmed;
        sent = probe.confirmed;
        stalledAttempts = 0;
        report(UPLOAD_PHASES.UPLOADING);
        continue;
      }

      if (probe.state === "expired") {
        confirmed = 0;
        sent = 0;
        report(UPLOAD_PHASES.FAILED);
        return {
          ok: false,
          name,
          code: "creative_upload_session_expired",
          title: "Upload session expired",
          message: "Cphere needs to reconnect this creative before continuing.",
          action: "retry",
          confirmedBytes: 0,
          total,
          uploadSessionId: session.id,
        };
      }

      stalledAttempts += 1;
      if (stalledAttempts < MAX_CHUNK_ATTEMPTS) {
        await sleep(RETRY_BASE_MS * stalledAttempts);
        continue;
      }

      // Out of attempts. Report exactly what the provider last confirmed.
      if (probe.state === "partial" || confirmed > 0) {
        const durable = probe.state === "partial" ? probe.confirmed : confirmed;
        confirmed = durable;
        sent = durable;
        report(UPLOAD_PHASES.FAILED);
        return {
          ok: false,
          name,
          code: "creative_upload_interrupted_resumable",
          title: "Upload interrupted",
          message:
            "The connection was interrupted. Your upload can resume from where it stopped.",
          action: "resume",
          confirmedBytes: durable,
          total,
          uploadSessionId: session.id,
        };
      }

      if (probe.state === "empty") {
        confirmed = 0;
        sent = 0;
        report(UPLOAD_PHASES.FAILED);
        return {
          ok: false,
          name,
          code: "creative_upload_interrupted_empty",
          title: "Upload interrupted",
          message:
            "The connection was interrupted before the upload could be saved. You can safely try again.",
          action: "retry",
          confirmedBytes: 0,
          total,
          uploadSessionId: session.id,
        };
      }

      // The server could not be reached either. Not knowing is not the same
      // as knowing nothing was saved, and must never be reported as such.
      report(UPLOAD_PHASES.FAILED);
      return {
        ok: false,
        name,
        code: "creative_upload_interrupted_unknown",
        title: "Upload interrupted",
        message:
          "The connection was interrupted and Cphere could not confirm what was saved. Resuming will continue from wherever storage left off.",
        action: "resume",
        confirmedBytes: null,
        total,
        uploadSessionId: session.id,
      };
    }

    stalledAttempts = 0;
    // Bytes left this browser; durability is decided by what storage says.
    sent = end;

    if (response.status === 308) {
      const range = response.headers?.get?.("Range");
      const match = range ? String(range).match(/bytes=0-(\d+)/) : null;
      confirmed = match ? Number(match[1]) + 1 : end;
      report(UPLOAD_PHASES.UPLOADING);
      continue;
    }

    if (response.status === 200 || response.status === 201) {
      confirmed = total;
      sent = total;
      report(UPLOAD_PHASES.UPLOADING);
      break;
    }

    if (response.status === 404 || response.status === 410) {
      confirmed = 0;
      sent = 0;
      report(UPLOAD_PHASES.FAILED);
      return {
        ok: false,
        name,
        code: "creative_upload_session_expired",
        title: "Upload session expired",
        message: "Cphere needs to reconnect this creative before continuing.",
        action: "retry",
        confirmedBytes: 0,
        total,
        uploadSessionId: session.id,
      };
    }

    // Any other status is storage refusing this file outright.
    report(UPLOAD_PHASES.FAILED);
    return {
      ok: false,
      name,
      code: "creative_upload_rejected_by_storage",
      title: "Storage refused this file",
      message:
        "Storage refused this file. Retrying is safe; if it keeps failing, re-export the file.",
      action: "retry",
      confirmedBytes: 0,
      total,
      uploadSessionId: session.id,
    };
  }

  // Every byte is now in storage and the server is checking what it holds
  // against what was declared. That is a genuinely different wait from the
  // transfer, and naming it is what keeps a completed 716.6 MB upload from
  // looking frozen at 100%. FINALIZING is no longer emitted here: it now
  // belongs to the page, which owns the library registration that follows.
  report(UPLOAD_PHASES.VERIFYING);

  try {
    const finalized = await api.finalizeUploadSession({ sessionId: session.id, advertiserId });
    confirmed = total;
    sent = total;
    report(UPLOAD_PHASES.DONE);
    return {
      ok: true,
      name,
      assetId: finalized?.data?.id || null,
      duplicate: finalized?.duplicate || null,
      uploaded: true,
    };
  } catch (error) {
    report(UPLOAD_PHASES.FAILED);
    // The BYTES are in storage — only the server-side completion step failed.
    // Saying "nothing was saved" here would be false in exactly the way that
    // produced the production report; the upload does not need repeating,
    // it needs completing.
    return {
      ok: false,
      name,
      code: error?.code || "creative_upload_finalize_failed",
      title: "Upload saved, not yet completed",
      message:
        error?.message ||
        "Your file finished uploading but Cphere could not complete it. The upload is safe in storage; finishing will not re-send it.",
      action: "resume",
      confirmedBytes: confirmed,
      total,
      uploadSessionId: error?.uploadSessionId || session.id,
    };
  }
}

/**
 * Upload a selection with BOUNDED concurrency.
 *
 * Six files must never become one uncontrolled ~2.93GB burst. A small worker
 * pool keeps a predictable number of transfers in flight; each file still
 * succeeds or fails entirely on its own.
 */
export async function uploadCreativeBatch({
  files,
  advertiserId,
  titleFor = (file) => file.name,
  notes,
  batchId,
  policy,
  metadataFor = async () => null,
  api,
  fetchImpl = fetch,
  signal = null,
  onProgress = () => {},
  concurrency = null,
}) {
  const list = Array.from(files || []);
  const limit = Math.max(1, Number(concurrency || policy?.maxConcurrentUploads || 2));
  const results = new Array(list.length);
  let cursor = 0;

  const worker = async () => {
    while (cursor < list.length) {
      const index = cursor;
      cursor += 1;
      const file = list[index];
      results[index] = await uploadOneCreative({
        file,
        advertiserId,
        title: titleFor(file, index),
        notes,
        batchId,
        policy,
        metadata: await metadataFor(file),
        api,
        fetchImpl,
        signal,
        onProgress: (update) => onProgress({ ...update, index }),
      });
    }
  };

  await Promise.all(Array.from({ length: Math.min(limit, list.length) }, worker));
  return results;
}

export default uploadCreativeBatch;
